import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, FileDown, Lock, Clock, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, currency = "EUR") =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency, minimumFractionDigits: 2 }).format(Number(n) || 0);

export default function PublicQuote() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/quotes/${token}`);
        setQuote(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Impossibile caricare il preventivo.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/quotes/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la creazione del pagamento");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <Card className="rounded-sm max-w-md w-full p-8 border-red-200 bg-red-50" data-testid="public-quote-error">
          <div className="text-[10px] uppercase tracking-widest text-red-700 mb-2">Errore</div>
          <div className="text-red-900 font-medium">{error}</div>
        </Card>
      </div>
    );
  }

  const currency = quote.currency || "EUR";
  const paid = quote.payment_status === "paid";

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <BrandLogo className="h-9 w-9" />
          <div>
            <div className="font-display text-base font-semibold text-zinc-950 leading-tight">
              {quote.company_name || "Wineemotion"} · Assistenza
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Preventivo per il cliente</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        {/* Header preventivo */}
        <Card className="rounded-sm p-6 border-zinc-200" data-testid="public-quote-header">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Preventivo</div>
              <div className="font-mono text-lg font-semibold text-zinc-950 mt-0.5" data-testid="public-quote-number">
                {quote.quote_number}
              </div>
              {quote.customer_name && (
                <div className="text-sm text-zinc-700 mt-2">Intestato a: <b>{quote.customer_name}</b></div>
              )}
              {quote.dispenser_serial && (
                <div className="text-xs text-zinc-500 mt-1">Dispenser: <span className="font-mono">{quote.dispenser_serial}</span></div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Totale</div>
              <div className="font-display text-3xl font-bold text-brand mt-1" data-testid="public-quote-total">
                {fmt(quote.total, currency)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">IVA {quote.vat_pct}% inclusa</div>
            </div>
          </div>
        </Card>

        {/* Righe */}
        {quote.items?.length > 0 && (
          <Card className="rounded-sm p-6 border-zinc-200" data-testid="public-quote-items">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Ricambi</div>
            <div className="divide-y divide-zinc-100">
              {quote.items.map((it) => (
                <div key={it.code} className="py-2 flex items-start justify-between gap-4 text-sm">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-zinc-500">{it.code}</div>
                    <div className="text-zinc-900">{it.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-zinc-500">{it.qty} × {fmt(it.unit_price, currency)}</div>
                    <div className="font-mono text-zinc-900">{fmt((it.qty || 0) * (it.unit_price || 0), currency)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Manodopera + totali */}
        <Card className="rounded-sm p-6 border-zinc-200" data-testid="public-quote-totals">
          <div className="space-y-1.5 text-sm">
            {quote.labor_hours > 0 && (
              <div className="flex items-center justify-between text-zinc-700">
                <span>Manodopera ({quote.labor_hours}h × {fmt(quote.labor_rate, currency)})</span>
                <span className="font-mono">{fmt(quote.subtotal_labor, currency)}</span>
              </div>
            )}
            {quote.subtotal_parts > 0 && (
              <div className="flex items-center justify-between text-zinc-700">
                <span>Ricambi</span>
                <span className="font-mono">{fmt(quote.subtotal_parts, currency)}</span>
              </div>
            )}
            {quote.discount > 0 && (
              <div className="flex items-center justify-between text-zinc-700">
                <span>Sconto {quote.discount_pct}%</span>
                <span className="font-mono">-{fmt(quote.discount, currency)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-zinc-700 border-t pt-2 mt-2">
              <span>Imponibile</span>
              <span className="font-mono">{fmt(quote.total_before_vat, currency)}</span>
            </div>
            <div className="flex items-center justify-between text-zinc-700">
              <span>IVA {quote.vat_pct}%</span>
              <span className="font-mono">{fmt(quote.vat, currency)}</span>
            </div>
            <div className="flex items-center justify-between text-zinc-950 border-t pt-2 mt-2 font-semibold text-base">
              <span>Totale</span>
              <span className="font-mono">{fmt(quote.total, currency)}</span>
            </div>
          </div>

          {quote.notes && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Note</div>
              <div className="text-sm text-zinc-700 whitespace-pre-wrap">{quote.notes}</div>
            </div>
          )}
        </Card>

        {/* Azione pagamento */}
        {paid ? (
          <Card className="rounded-sm p-6 border-emerald-200 bg-emerald-50" data-testid="public-quote-paid">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Preventivo già pagato</div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Il pagamento risulta ricevuto. Non è necessaria alcuna ulteriore azione.
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="rounded-sm p-6 border-brand/30 bg-white">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-semibold text-zinc-950">Pagamento sicuro con Stripe</div>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
                  <Lock className="h-3 w-3" /> Connessione cifrata · Carte, Apple Pay, Google Pay
                </div>
              </div>
              <Button
                onClick={pay}
                disabled={paying}
                className="bg-brand hover:bg-brand-dark text-white rounded-sm h-11 px-6 font-semibold"
                data-testid="public-quote-pay-btn"
              >
                {paying ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Attendere...</>
                ) : (
                  <><CreditCard className="h-4 w-4 mr-2" /> Paga {fmt(quote.total, currency)}</>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Download PDF */}
        <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
          <a
            href={`${API_BASE}/public/quotes/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-brand hover:text-brand-dark"
            data-testid="public-quote-pdf-link"
          >
            <FileDown className="h-3.5 w-3.5" /> Scarica PDF preventivo
          </a>
          {quote.expires_at && (
            <div className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> Link valido fino al {new Date(quote.expires_at).toLocaleDateString("it-IT")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
